import { serializePaginationParams } from '@/shared/api/pagination';

import { isSearchParamEnabledForPage } from '../search-pages';
import type {
  AppSearchAction,
  AppSearchProvider,
  AppSearchSelectField,
} from '../types';

export const SEARCH_PAGE_SIZE = 30;

export const ilikeFilters = (query: string, fields: string[]) => {
  const wrapped = `%${query}%`;

  return fields.map((field) => ({
    field,
    op: 'ilike' as const,
    value: wrapped,
    condition: 'or' as const,
  }));
};

export const pageSearchParams = (query: string, fields: string[]) =>
  serializePaginationParams({
    page: 1,
    size: SEARCH_PAGE_SIZE,
    filter: query ? ilikeFilters(query, fields) : undefined,
  });

export const DEFAULT_SELECT_PARAM = 'selected';

export const createPageSearchProvider = ({
  id,
  attachTo,
  mode,
  minQueryLength = 1,
  select = 'id',
  selectParam = DEFAULT_SELECT_PARAM,
  search,
  cacheQuery,
  mapCache,
}: {
  id: string;
  attachTo: string;
  mode: 'page' | 'client' | 'cache';
  minQueryLength?: number;
  select?: AppSearchSelectField;
  selectParam?: string;
  search?: AppSearchProvider['search'];
  cacheQuery?: AppSearchProvider['cacheQuery'];
  mapCache?: AppSearchProvider['mapCache'];
}): AppSearchProvider => ({
  id,
  attachTo,
  mode,
  minQueryLength,
  select,
  selectParam,
  search,
  cacheQuery,
  mapCache,
});

export const searchHref = (to: string, value: string) =>
  `${to}?search=${encodeURIComponent(value)}`;

export const selectHref = (
  to: string,
  value: string,
  param = DEFAULT_SELECT_PARAM,
) => `${to}?${param}=${encodeURIComponent(value)}`;

export const itemSelectValue = (
  item: { id?: string; name?: string; label?: string },
  select: AppSearchSelectField = 'id',
) =>
  select === 'name'
    ? item.name || item.label || ''
    : item.id || item.name || '';

export const toItemAction = ({
  id,
  label,
  description,
  keywords,
  children,
  href,
  searchHref: searchHrefValue,
  page,
  searchValue,
  selectedId,
  select = 'id',
  selectParam = DEFAULT_SELECT_PARAM,
  item,
  onSelect,
}: {
  id: string;
  label: string;
  description?: string;
  keywords?: string[];
  children?: AppSearchAction[];
  href?: string;
  searchHref?: string;
  page?: string;
  searchValue?: string;
  selectedId?: string;
  select?: AppSearchSelectField;
  selectParam?: string;
  item?: { id?: string; name?: string; label?: string };
  onSelect?: () => void;
}): AppSearchAction => {
  const resolvedSelectedId =
    selectedId ?? (item ? itemSelectValue(item, select) : undefined);
  const allowSearch = isSearchParamEnabledForPage(page);
  const resolvedSearchHref = allowSearch
    ? (searchHrefValue ??
      (page && searchValue ? searchHref(page, searchValue) : undefined))
    : undefined;
  const resolvedHref =
    href ??
    (page && resolvedSelectedId
      ? selectHref(page, resolvedSelectedId, selectParam)
      : resolvedSearchHref);

  return {
    id: resolvedSelectedId ? `${id}:${resolvedSelectedId}` : id,
    label,
    description,
    keywords: [label, description, ...(keywords ?? [])].filter(
      (value): value is string => Boolean(value),
    ),
    path: [label],
    completion: label,
    enterable: Boolean(children?.length),
    children,
    href: resolvedHref,
    searchHref: resolvedSearchHref,
    onSelect: onSelect ?? (() => undefined),
  };
};